import { Box, Skeleton, Typography } from '@mui/material';
import { Chart, registerables } from 'chart.js';
import { ReactNode } from 'react';

import Card from '@mui/material/Card';
import { Line } from 'react-chartjs-2';
import colors from 'assets/theme/colors';
import configs from './configs';

Chart.register(...registerables);

// Declaring props types for DefaultLineChart
interface Props {
    icon?: {
        color?:
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark';
        component: ReactNode;
    };
    title?: string;
    description?: string | ReactNode;
    height?: string | number;
    titleColor?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'light'
    | 'dark'
    | 'white';
    chart: {
        labels: string[];
        datasets: {
            label: string;
            color:
            | 'primary'
            | 'secondary'
            | 'info'
            | 'success'
            | 'warning'
            | 'error'
            | 'light'
            | 'dark';
            data: Array<number | null>;
        }[];
    };
    [key: string]: any;
}

function LineChart({
    icon,
    title,
    description,
    height,
    chart,
    titleColor,
}: Props): JSX.Element {
    const chartDatasets = chart?.datasets
        ? chart.datasets.map((dataset) => ({
            ...dataset,
            tension: 0,
            pointRadius: 3,
            borderWidth: 4,
            backgroundColor: 'transparent',
            fill: true,
            pointBackgroundColor: colors[dataset.color]
                ? colors[dataset.color || 'dark'].main
                : colors.dark.main,
            borderColor: colors[dataset.color]
                ? colors[dataset.color || 'dark'].main
                : colors.dark.main,
            maxBarThickness: 6,
        }))
        : [];

    const { data, options } = configs(chart?.labels || [], chartDatasets);

    const renderChart = (
        <Box py={2} pr={2} pl={icon?.component ? 1 : 2}>
            {title || description ? (
                <Box
                    display="flex"
                    px={description ? 1 : 0}
                    pt={description ? 1 : 0}
                >
                    {icon?.component && (
                        <Box
                            sx={{
                                backgroundColor: icon.color
                                    ? colors[icon.color].main
                                    : colors['info'].main,
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: 'white',
                                mt: -1,
                                mr: 2,
                                height: 35,
                                width: 35,
                                flexShrink: 0,
                            }}
                        >
                            <Box aria-hidden="true" display="flex" alignItems="center" justifyContent="center">
                                {icon.component}
                            </Box>
                        </Box>
                    )}
                    <Box mt={icon?.component ? -2 : 0} width="100%">
                        {title && (
                            <Typography
                                sx={{
                                    display: 'inline',
                                    fontSize: '1rem',
                                }}
                                component="div"
                                variant="h5"
                                color="text.primary"
                            >
                                {title}
                            </Typography>
                        )}
                        <Box mb={2}>
                            <Typography
                                sx={{ display: 'inline', fontSize: '0.8rem' }}
                                component="div"
                                variant="h5"
                                color="text.secondary"
                            >
                                {description}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : null}
            <Box height={height} width={window.innerWidth * 0.9}>
                <Line data={data} options={options as any} />
            </Box>
        </Box>
    );

    return chart ? (
        title || description ? (
            <Card>{renderChart}</Card>
        ) : (
            renderChart
        )
    ) : (
        <Skeleton variant="rectangular" width="100%" height={height} />
    );
}

// Declaring default props DefaultLineChart
LineChart.defaultProps = {
    icon: { color: 'info', component: '' },
    title: '',
    description: '',
    height: '30vh',
};

export default LineChart;
